/**
 * Batch fetch logic for useUISpec
 *
 * Extracted from use-ui-spec.ts to comply with <300 lines/file rule.
 * Handles the standard (non-streaming) batch mode for fetching UI specs.
 *
 * Task: NocoDB #2675 — file split
 * Agent: Diego <diego@liftping.net>
 */

import { authenticatedFetch, getEdgeFunctionUrl } from "@dramadetector/api-client";
import type { Spec } from "@json-render/core";
import type { DramaAnalysisResponse } from "@dramadetector/shared";
import type { UISpecTier } from "./use-ui-spec-utils";

/**
 * Parameters needed by the batch fetch function.
 * These are passed from the useUISpec hook closure.
 */
export interface BatchFetchParams {
  analysisId: string;
  analysisResult: DramaAnalysisResponse;
  userTier: UISpecTier;
  abortController: AbortController;
  isCancelled: () => boolean;
  onSpec: (spec: Spec) => void;
  onLoadingEnd: () => void;
  clearMainTimeout: () => void;
}

/**
 * Fetch UI spec via standard batch mode (generateObject returns complete spec).
 *
 * This is the original behavior — a single POST request that returns
 * the full spec as JSON.
 */
export async function fetchBatchSpec(params: BatchFetchParams): Promise<void> {
  const {
    analysisId,
    analysisResult,
    userTier,
    abortController,
    isCancelled,
    onSpec,
    onLoadingEnd,
    clearMainTimeout,
  } = params;

  const url = getEdgeFunctionUrl('generate-ui-spec');

  const response = await authenticatedFetch(url, {
    method: 'POST',
    signal: abortController.signal,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Version': 'v2',
    },
    body: JSON.stringify({
      analysisId,
      analysisResult,
      userTier,
    }),
  });

  // Guard: timeout may have already fired or component unmounted
  if (isCancelled()) return;

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(
      `generate-ui-spec error: ${(errBody as Record<string, string>)?.error || response.statusText}`
    );
  }

  const data = await response.json();

  if (!data?.spec) {
    throw new Error("generate-ui-spec returned no spec");
  }

  // Success — cancel timeout and set spec
  clearMainTimeout();
  onSpec(data.spec as Spec);
  onLoadingEnd();
}
